import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import {
  HtsCatalogRow,
  ListCatalogRow,
  SavedFilter,
  SavedFilterKind,
  Scope,
  ScopeKey,
} from './types';
import { Jurisdiction } from './profiles';
import {
  CascadeColumn,
  HTS_KEYS,
  LIST_KEYS,
  cascade,
  emptyScope,
  savedMatches,
  scopeFromSaved,
} from './utils';
import ScreeningDialog from './ScreeningDialog';

interface Props {
  scope: Scope;
  // The import or export screen's market: whether HTS codes can be picked and
  // whether the record total is known. Without one (Entity Screening) the
  // modal narrows by lists only.
  jurisdiction?: Jurisdiction;
  // What a saved list selection is saved as; `commodityList` by default.
  listKind?: SavedFilterKind;
  countryTitle?: string;
  listRows: ListCatalogRow[];
  htsRows?: HtsCatalogRow[];
  codeLists?: { key: string; n: number }[];
  loading: boolean;
  recordTotal: number | null;
  savedFilters: SavedFilter[];
  onCreateSaved: (
    kind: SavedFilterKind,
    name: string,
    s: Scope,
  ) => Promise<SavedFilter | null>;
  onUpdateSaved: (f: SavedFilter, s: Scope) => Promise<void>;
  onDeleteSaved: (f: SavedFilter) => Promise<void>;
  applyLabel: string;
  onApply: (s: Scope) => void;
  onCancel: () => void;
}

// A screen narrows by lists or by HTS codes, never both at once.
type FilterMode = 'list' | 'hts';

const MODES: {
  id: FilterMode;
  label: string;
  noun: string;
  kind: SavedFilterKind;
  keys: ScopeKey[];
}[] = [
  {
    id: 'list',
    label: 'Lists',
    noun: 'list',
    kind: 'commodityList',
    keys: LIST_KEYS,
  },
  {
    id: 'hts',
    label: 'HTS codes',
    noun: 'HTS code',
    kind: 'commodityCode',
    keys: HTS_KEYS,
  },
];

const RENDER_CAP = 120;
const SKELETON_ROWS = [72, 54, 86, 61, 78, 48];
const NO_HTS_ROWS: HtsCatalogRow[] = [];
const NO_CODE_LISTS: { key: string; n: number }[] = [];

const countKeys = (s: Scope, keys: ScopeKey[]) =>
  keys.reduce((t, k) => t + s[k].length, 0);

const withOnly = (s: Scope, keys: ScopeKey[]): Scope => {
  const out = emptyScope();
  keys.forEach((k) => {
    out[k] = s[k].slice();
  });
  return out;
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

interface ColumnProps {
  col: CascadeColumn;
  loading: boolean;
  onToggle: (dim: ScopeKey, v: string) => void;
}

const Column = React.memo(({ col, loading, onToggle }: ColumnProps) => {
  const [q, setQ] = useState('');
  const term = useDeferredValue(q).trim().toLowerCase();
  const { visible, matching } = useMemo(() => {
    const hits = term
      ? col.options.filter((o) => o.key.toLowerCase().indexOf(term) !== -1)
      : col.options;
    const on = hits.filter((o) => o.on);
    const off = hits.filter((o) => !o.on);
    return {
      visible: on.concat(off.slice(0, Math.max(0, RENDER_CAP - on.length))),
      matching: hits.length,
    };
  }, [col.options, term]);
  const noun = col.title.toLowerCase().replace('hts', 'HTS');
  const waiting = loading && col.options.length === 0;

  return (
    <div className='entity-results-filter-modal-column'>
      <div className='entity-results-filter-modal-column-header'>
        <h3>{col.title}</h3>
        <span className='tnum'>
          {col.selected
            ? `${col.selected} selected`
            : `All ${col.options.length.toLocaleString()}`}
        </span>
      </div>
      <div className='es2-filter-column-search'>
        <SearchIcon sx={{ fontSize: 16 }} />
        <input
          type='search'
          value={q}
          disabled={waiting}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${noun}`}
          aria-label={`Search ${noun}`}
        />
      </div>
      <div
        className='entity-results-filter-modal-options'
        role='group'
        aria-label={col.title}
        aria-busy={waiting || undefined}
      >
        {waiting &&
          SKELETON_ROWS.map((w, i) => (
            <div key={i} className='psc-filters-skel' aria-hidden='true'>
              <i />
              <b style={{ width: `${w}%` }} />
            </div>
          ))}
        {!waiting &&
          visible.map((o) => (
            <button
              type='button'
              key={o.key}
              className='entity-results-filter-modal-option'
              role='checkbox'
              aria-checked={o.on}
              onClick={() => onToggle(col.dim, o.key)}
            >
              <span className='bill-container-fit-screen-content-row-input'>
                <span
                  className={`bill-container-fit-screen-content-row-input-icon${
                    o.on ? ' selected' : ''
                  }`}
                >
                  <span
                    className={`bill-container-fit-screen-content-row-input-icon-rnd${
                      o.on ? ' selected' : ''
                    }`}
                  />
                </span>
              </span>
              <span className='entity-results-filter-modal-option-text'>
                {o.key}
              </span>
              <span className='psc-filters-n tnum'>{o.n.toLocaleString()}</span>
            </button>
          ))}
        {!waiting && matching > visible.length && (
          <p className='psc-filters-cap tnum'>
            Showing the largest {visible.length.toLocaleString()} of{' '}
            {matching.toLocaleString()}. Search to find the rest.
          </p>
        )}
        {!waiting && matching === 0 && (
          <p>
            {col.options.length
              ? `Nothing here matches “${q.trim()}”.`
              : 'Nothing to choose from.'}
          </p>
        )}
      </div>
    </div>
  );
});
Column.displayName = 'Column';

const FiltersModal: React.FC<Props> = (props) => {
  const listsOnly = !props.jurisdiction;
  const htsAllowed = Boolean(props.jurisdiction?.htsFilters);
  const countsRecords = props.jurisdiction
    ? props.jurisdiction.countsRecords
    : true;
  const htsRows = props.htsRows || NO_HTS_ROWS;
  const codeLists = props.codeLists || NO_CODE_LISTS;
  const [draft, setDraft] = useState<Scope>(() =>
    JSON.parse(JSON.stringify(props.scope)),
  );
  const [mode, setMode] = useState<FilterMode>(() =>
    htsAllowed &&
    countKeys(props.scope, HTS_KEYS) > 0 &&
    countKeys(props.scope, LIST_KEYS) === 0
      ? 'hts'
      : 'list',
  );
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string | null>(null);

  const active = MODES.find((m) => m.id === mode) || MODES[0];
  const activeKind =
    active.id === 'list' && props.listKind ? props.listKind : active.kind;

  const toggle = useCallback((dim: ScopeKey, v: string) => {
    setDraft((d) => {
      const cur = d[dim];
      const next =
        cur.indexOf(v) === -1 ? cur.concat([v]) : cur.filter((x) => x !== v);
      return { ...d, [dim]: next };
    });
  }, []);

  const { lens, country, agency, list, hlist, section, chapter, code } = draft;

  const listCols = useMemo(
    () =>
      cascade(
        props.listRows,
        [
          { key: 'lens', title: 'Lens' },
          { key: 'country', title: props.countryTitle || 'Listing country' },
          { key: 'agency', title: 'Agency' },
          { key: 'list', title: 'List' },
        ],
        {
          ...emptyScope(),
          lens: lens,
          country: country,
          agency: agency,
          list: list,
        },
      ),
    [props.listRows, props.countryTitle, lens, country, agency, list],
  );

  const htsCols = useMemo<CascadeColumn[]>(
    () => [
      {
        dim: 'hlist',
        title: 'HTS code list',
        selected: hlist.length,
        options: codeLists
          .slice()
          .sort((a, b) => b.n - a.n)
          .map((c) => ({
            key: c.key,
            n: c.n,
            on: hlist.indexOf(c.key) !== -1,
          })),
      },
      ...cascade(
        htsRows,
        [
          { key: 'section', title: 'Section' },
          { key: 'chapter', title: 'Chapter' },
          { key: 'code', title: 'HTS code' },
        ],
        { ...emptyScope(), section: section, chapter: chapter, code: code },
      ),
    ],
    [codeLists, htsRows, hlist, section, chapter, code],
  );

  const cols = mode === 'list' ? listCols : htsCols;
  const n = countKeys(draft, active.keys);
  const applied = withOnly(draft, active.keys);

  const saved = props.savedFilters.filter((f) => f.type === activeKind);
  const matched = saved.find((f) => savedMatches(f, draft));
  const edited = saved.find((f) => f._id === picked && !savedMatches(f, draft));

  const inScope = props.listRows
    .filter((r) =>
      LIST_KEYS.every(
        (k) => draft[k].length === 0 || draft[k].indexOf((r as any)[k]) !== -1,
      ),
    )
    .reduce((t, r) => t + r.n, 0);
  const everything =
    countsRecords && props.recordTotal !== null
      ? `No filters · ${props.recordTotal.toLocaleString()} records in scope`
      : 'No filters · every list is screened';
  const preview = !n
    ? everything
    : mode === 'list'
    ? `${plural(n, 'list filter')} · ${inScope.toLocaleString()} records`
    : `${plural(n, 'HTS code filter')} selected`;

  const switchMode = (next: FilterMode) => {
    if (next === mode || (next === 'hts' && !htsAllowed)) {
      return;
    }
    setMode(next);
    setNaming(false);
    setPicked(null);
  };

  const saveSelection = async () => {
    if (!name.trim()) {
      return;
    }
    const made = await props.onCreateSaved(activeKind, name.trim(), applied);
    if (made) {
      setNaming(false);
      setName('');
      setPicked(made._id);
    }
  };

  return (
    <ScreeningDialog
      labelledBy='psc-filters-title'
      panelClassName='entity-results-filter-modal screening-v2 psc-filters'
      onClose={props.onCancel}
    >
      <div className='entity-results-filter-modal-header'>
        <div className='entity-results-filter-modal-header-content'>
          <div className='entity-results-filter-modal-icon'>
            <FilterListIcon sx={{ fontSize: 24 }} />
          </div>
          <div className='entity-results-filter-modal-copy'>
            <h2 id='psc-filters-title'>Filter the screen</h2>
            <p>
              {listsOnly
                ? 'Each column narrows the one to its right, and applying re-runs the screen so the boxes above update with it.'
                : 'Narrow by lists or by HTS codes, one at a time. Each column narrows the one to its right, and applying re-runs the screen so the boxes above update with it.'}
            </p>
          </div>
        </div>
        <button
          type='button'
          className='entity-results-filter-modal-close'
          aria-label='Close filters'
          onClick={props.onCancel}
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </button>
      </div>

      <div className='es2-filter-band'>
        {!listsOnly && (
          <div
            className='psc-filters-mode'
            role='radiogroup'
            aria-label='Filter by'
          >
            <strong>Filter by</strong>
            {MODES.map((m) => {
              const locked = m.id === 'hts' && !htsAllowed;
              return (
                <button
                  type='button'
                  key={m.id}
                  role='radio'
                  aria-checked={mode === m.id}
                  className={mode === m.id ? 'active' : ''}
                  disabled={locked}
                  aria-describedby={
                    locked ? 'psc-filters-hts-note' : undefined
                  }
                  onClick={() => switchMode(m.id)}
                >
                  {m.label}
                </button>
              );
            })}
            {!htsAllowed && (
              <small id='psc-filters-hts-note' className='psc-filters-hint'>
                HTS codes apply to US data only
              </small>
            )}
          </div>
        )}
        <div className='es2-saved-filter-band psc-filters-saved'>
          <strong>Saved</strong>
          {saved.length ? (
            saved.map((f) => (
              <span
                className={`es2-saved-filter-chip${
                  matched && matched._id === f._id ? ' is-on' : ''
                }`}
                key={f._id}
              >
                <button
                  type='button'
                  aria-pressed={Boolean(matched && matched._id === f._id)}
                  onClick={() => {
                    setDraft(scopeFromSaved(f, draft));
                    setPicked(f._id);
                  }}
                >
                  {f.filterName}
                </button>
                <button
                  type='button'
                  aria-label={`Delete saved filter ${f.filterName}`}
                  onClick={() => props.onDeleteSaved(f)}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </button>
              </span>
            ))
          ) : (
            <em className='psc-filters-hint'>No saved {active.noun} filters</em>
          )}
        </div>
      </div>

      <div
        key={mode}
        className='entity-results-filter-modal-body psc-filters-body'
        role='group'
        aria-label={`${active.label} filters`}
      >
        {cols.map((c) => (
          <Column
            key={c.dim}
            col={c}
            loading={props.loading}
            onToggle={toggle}
          />
        ))}
      </div>

      <div className='entity-results-filter-modal-footer'>
        {naming ? (
          <>
            <input
              type='text'
              className='psc-filters-name'
              placeholder={`Name this ${active.noun} filter`}
              aria-label={`Name this ${active.noun} filter`}
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveSelection();
                } else if (e.key === 'Escape') {
                  e.stopPropagation();
                  setNaming(false);
                }
              }}
            />
            <button type='button' onClick={() => setNaming(false)}>
              Cancel
            </button>
            <button
              type='button'
              disabled={!name.trim()}
              onClick={saveSelection}
            >
              Save filter
            </button>
          </>
        ) : (
          <>
            <span className='es2-filter-preview tnum' aria-live='polite'>
              {preview}
            </span>
            <button
              type='button'
              disabled={n === 0}
              onClick={() => {
                setDraft(emptyScope());
                setPicked(null);
              }}
            >
              Clear all
            </button>
            {edited ? (
              <button
                type='button'
                onClick={() => props.onUpdateSaved(edited, applied)}
              >
                Update “{edited.filterName}”
              </button>
            ) : (
              <button
                type='button'
                disabled={n === 0 || Boolean(matched)}
                title={matched ? 'This selection is already saved' : undefined}
                onClick={() => {
                  setNaming(true);
                  setName('');
                }}
              >
                Save as filter
              </button>
            )}
            <button type='button' onClick={props.onCancel}>
              Cancel
            </button>
            <button type='button' onClick={() => props.onApply(applied)}>
              {props.applyLabel}
            </button>
          </>
        )}
      </div>
    </ScreeningDialog>
  );
};

export default FiltersModal;
